import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AuthModal from '../AuthModal';
import ShareVersePicker from '../ShareVersePicker';
import type { SelectedVerse, ShareVersePickerHandle } from '../ShareVersePicker';
import MailCardAuthGate from './MailCardAuthGate';
import MailCardVerseQuestion from './MailCardVerseQuestion';
import MailCardCardLabelQuestion from './MailCardCardLabelQuestion';
import MailCardPreview from './MailCardPreview';
import MailCardRecipientForm from './MailCardRecipientForm';
import MailCardSenderForm from './MailCardSenderForm';
import MailCardConfirmReview from './MailCardConfirmReview';
import { getSupabase } from '@/lib/supabase';
import type { Prayer } from '@/types/prayer';
import type { UsAddressInput } from '@/lib/us-address-validate';
import { stripCardLabelDisambig } from '@/lib/card-label';
import { isCrisisPrayer } from '@/lib/spine-crisis';
import { VERSE_SUGGEST_ENABLED } from '@/lib/feature-flags';
import { useNavLayer } from '@/hooks/useNavLayer';
import { useNoticeStackReserve } from '@/hooks/useNoticeStackReserve';
import styles from './MailCardSheet.module.css';

/**
 * Phase 2 sub-chunks 2A + 2B + 2C. Top-level "Mail a card"
 * surface replacing the prior Picker mount inside ChoiceSheet's
 * mail-a-card branch.
 *
 * SHELL: full-screen overlay (position: fixed; inset: 0) matching
 * LetThemKnowSheet's pattern. The 2B post-merge fix-up refactored
 * away from SheetShell because the bottom-sheet's ~90dvh cap
 * could not hold the multi-page preview + question stack on
 * iPhone SE. Full-screen overlay with internal scrollable body
 * solves it.
 *
 * 2A scope (shipped 9e3e529): auth gate. (2A also shipped a Q0
 * sender-mode question; the 2026-05-29 removal of the
 * name-hidden sender posture deleted it — known-sender is now
 * the only mode, so the flow opens directly on Q1.)
 * 2B scope (shipped 2438332 + hotfix 1088973): Q1 verse + Q2
 * summary + MailCardPreview + edge-only auth handler.
 * 2C scope: Q3 recipient form + Q4 sender form + Confirm review
 * screen + Q1/Q2 commit-and-advance (was commit-and-close).
 *
 * Flow (known-sender only): Q1 verse → Q2 cardLabel noun (skipped
 * when the prayer has no cardLabel) → Q3 recipient → Q4 sender →
 * Confirm. Q1 is the first step and has no Prev.
 *
 * Share gate: entry is caller-owned, while the final `handleConfirmSend`
 * calls `authorizeOutbound`, which re-reads the live store and requires the
 * prayer to still exist (`isLiveSharePrayer`). A prayer deleted mid-flow
 * closes the flow across PrayerDetail, Garden, and Remember entry paths.
 *
 * Auth posture: in-place sign-in via email OTP works end-to-end
 * via the onAuthStateChange listener (SIGNED_IN flips authPhase
 * to 'authed'). Google OAuth uses signInWithOAuth's full-page
 * redirect to window.location.origin (useAuth.ts:47-53) which
 * destroys the sheet's React tree; users land on home post-OAuth
 * and re-initiate Send-a-card by navigating back to the target
 * prayer.
 *
 * Q2 cardLabel conditional skip (plan § 3.3 + § 7): when the
 * prayer has no cardLabel noun, Q2 is structurally skipped and Q1
 * commit advances directly to Q3 (recipient form). Confirm
 * surfaces the `confirmCardLabelNotAvailable` copy explaining the
 * skip. The 2B empty-summary commit-and-close artifact is fixed
 * in 2C by the Q1/Q2 commit-and-advance flip.
 *
 * Phase 2C transitional copy keys (deleted in Phase 3 alongside
 * real checkout wiring):
 *   - confirmFinishPreviewBtn (CTA)
 *   - confirmTitleFinishPreview (Confirm heading)
 *   - confirmShowMyNameHelperFinishPreview (Confirm helper)
 * Phase 3 swaps each back to its Phase-1-shipped counterpart
 * (confirmSendBtn / confirmTitle / confirmShowMyNameHelper) in
 * the same PR that wires real /api/commerce/checkout.
 */

type AuthPhase = 'checking' | 'authed' | 'guest';
type Surface =
  | 'authGate'
  | 'preview'
  | 'versePicker'
  | 'recipientForm'
  | 'senderForm';
type Question =
  | 'bibleVerse'
  | 'cardLabel'
  | 'recipient'
  | 'sender'
  | 'confirm'
  | null;

const EMPTY_ADDRESS: UsAddressInput = {
  line1: '',
  line2: '',
  city: '',
  region: '',
  postalCode: '',
};

export interface MailCardRedactedPayload {
  prayerId: string;
  /** Boolean from Q2, OR false when Q2 was skipped (no cardLabel noun). */
  includesCardLabel: boolean;
  /** 0 or 1 in 2C (single-verse selection). */
  verseCount: number;
  /** True after Q3 commits. False before. */
  recipientPresent: boolean;
  /** True after Q4 commits. False before. */
  senderPresent: boolean;
}

interface MailCardSheetProps {
  prayer: Prayer;
  onClose: () => void;
  onContinue: (payload: MailCardRedactedPayload) => void;
  /** Re-read the live store immediately before the final send. */
  authorizeOutbound: () => boolean;
  /** Coordinator z for the share-mail / detail-share-mail layer root (A1). */
  zIndex?: number;
}

export default function MailCardSheet({
  prayer,
  onClose,
  onContinue,
  authorizeOutbound,
  zIndex,
}: MailCardSheetProps) {
  const { t, i18n } = useTranslation();
  // App notice stack (v0.2.0 finishing): the in-flow spacer below the header
  // keeps the preview and the question below the fixed notices (0 while no
  // notice shows). Chrome only.
  const [noticeReserve, noticeReserveRef] = useNoticeStackReserve();

  const hasClient = Boolean(getSupabase());
  const [authPhase, setAuthPhase] = useState<AuthPhase>(() =>
    hasClient ? 'checking' : 'guest',
  );
  const [surface, setSurface] = useState<Surface>('authGate');
  const [question, setQuestion] = useState<Question>(null);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  // mailcard-auth nested layer (A1): registered here; sheet mounts with it
  // closed -> StrictMode-safe. Browser-back closes the auth modal first
  // (matching the existing state-gated Escape handler below).
  const zMailcardAuth = useNavLayer('mailcard-auth', authModalOpen, () =>
    setAuthModalOpen(false),
  );

  const [selectedVerse, setSelectedVerse] =
    useState<SelectedVerse | null>(null);
  const [includeVerse, setIncludeVerse] = useState(false);
  const [includeCardLabel, setIncludeCardLabel] = useState(false);

  // 2C: PII state. All four slots clear on guest transition per
  // plan § 3.4 (round-2 BLOCKER 2 fix).
  const [recipientName, setRecipientName] = useState('');
  const [recipientAddress, setRecipientAddress] =
    useState<UsAddressInput>(EMPTY_ADDRESS);
  const [senderName, setSenderName] = useState('');
  const [senderAddress, setSenderAddress] =
    useState<UsAddressInput>(EMPTY_ADDRESS);

  const prevAuthPhaseRef = useRef<AuthPhase>('checking');

  useEffect(() => {
    const sb = getSupabase();
    if (!sb) return;
    let cancelled = false;

    const applyPhase = (sessionPresent: boolean) => {
      if (cancelled) return;
      const nextPhase: AuthPhase = sessionPresent ? 'authed' : 'guest';
      const prevPhase = prevAuthPhaseRef.current;
      prevAuthPhaseRef.current = nextPhase;

      setAuthPhase(nextPhase);

      if (nextPhase === 'authed' && prevPhase !== 'authed') {
        setSurface('preview');
        setQuestion('bibleVerse');
      } else if (nextPhase === 'guest') {
        setSurface('authGate');
        setQuestion(null);
        setSelectedVerse(null);
        setIncludeVerse(false);
        setIncludeCardLabel(false);
        // PII reset (plan § 3.4 + round-2 BLOCKER 2 fix).
        setRecipientName('');
        setRecipientAddress(EMPTY_ADDRESS);
        setSenderName('');
        setSenderAddress(EMPTY_ADDRESS);
      }
    };

    sb.auth
      .getSession()
      .then(({ data }) => applyPhase(Boolean(data.session)))
      .catch(() => applyPhase(false));

    const { data: subscription } = sb.auth.onAuthStateChange(
      (_event, session) => applyPhase(Boolean(session)),
    );

    return () => {
      cancelled = true;
      subscription.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (authModalOpen) {
        setAuthModalOpen(false);
      } else {
        onClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [authModalOpen, onClose]);

  // Mirror the text/share card's noun source: the curated cardLabel,
  // English-only (rule #7 second exception) and stripped of the
  // Grok-internal disambiguator. Null for es/pt and for abstract /
  // no-match English prayers. Null means Q2 is skipped and the card
  // shows no "about" line, matching texting. The 2026-05-29 product
  // decision replaced gardenSummary with this as the mail "about"
  // source. Computed once here and passed down so the skip-gate and
  // the rendered preview never disagree.
  const isEnglish = (i18n.language ?? 'en').slice(0, 2) === 'en';
  const cardLabelRaw = prayer.parsed?.cardLabel ?? null;
  const cardLabelNoun =
    isEnglish && cardLabelRaw ? stripCardLabelDisambig(cardLabelRaw) : null;

  // Find-Scripture fork eligibility (share-scripture-found arc, plan
  // decision 7; the PrayerDetail spineEligible composition, mirrored in
  // LetThemKnowSheet).
  const prayerIsCrisis = useMemo(
    () => isCrisisPrayer(prayer.rawText),
    [prayer.rawText],
  );
  const verseFindEligible =
    VERSE_SUGGEST_ENABLED &&
    isEnglish &&
    authPhase === 'authed' &&
    prayer.rawText.trim().length > 0 &&
    !prayerIsCrisis;
  const versePickerRef = useRef<ShareVersePickerHandle | null>(null);

  /**
   * Centralizes the question → surface mapping so per-row Edit
   * affordances on Confirm don't re-implement it. Plan § 8.
   *
   * Defensive: jumping to 'cardLabel' when the prayer has no
   * cardLabel noun reroutes to Q3 recipient form instead, matching
   * the forward-path advanceFromQ1 contract.
   */
  const goTo = (target: Question) => {
    switch (target) {
      case 'bibleVerse':
        setQuestion('bibleVerse');
        setSurface('preview');
        return;
      case 'cardLabel':
        if (cardLabelNoun === null) {
          setQuestion('recipient');
          setSurface('recipientForm');
          return;
        }
        setQuestion('cardLabel');
        setSurface('preview');
        return;
      case 'recipient':
        setQuestion('recipient');
        setSurface('recipientForm');
        return;
      case 'sender':
        setQuestion('sender');
        setSurface('senderForm');
        return;
      case 'confirm':
        setQuestion('confirm');
        setSurface('preview');
        return;
      case null:
        return;
    }
  };

  const handleVerseInclude = () => {
    setSurface('versePicker');
  };

  /**
   * Plan section 3.1: Q1 commit-and-advance (was commit-and-close
   * in 2B). With a cardLabel noun, go to Q2; without, go straight to
   * Q3 (recipient form). The empty-Q2 close-on-pick artifact is
   * fixed structurally.
   */
  const advanceFromQ1 = () => {
    if (cardLabelNoun !== null) {
      setQuestion('cardLabel');
      setSurface('preview');
      return;
    }
    setIncludeCardLabel(false);
    setQuestion('recipient');
    setSurface('recipientForm');
  };

  const handleVerseSkip = () => {
    setIncludeVerse(false);
    setSelectedVerse(null);
    advanceFromQ1();
  };

  const handleVersePick = (verse: SelectedVerse) => {
    setSelectedVerse(verse);
    setIncludeVerse(true);
    setSurface('preview');
    advanceFromQ1();
  };

  // 2C: Q2 commits-and-advances to Q3 (was commit-and-close in 2B).
  const handleCardLabelCommit = (nextInclude: boolean) => () => {
    setIncludeCardLabel(nextInclude);
    setQuestion('recipient');
    setSurface('recipientForm');
  };

  const handleRecipientChange = (next: {
    name: string;
    address: UsAddressInput;
  }) => {
    setRecipientName(next.name);
    setRecipientAddress(next.address);
  };

  const handleRecipientSubmit = () => {
    setQuestion('sender');
    setSurface('senderForm');
  };

  const handleSenderChange = (next: {
    name: string;
    address: UsAddressInput;
  }) => {
    setSenderName(next.name);
    setSenderAddress(next.address);
  };

  const handleSenderSubmit = () => {
    setQuestion('confirm');
    setSurface('preview');
  };

  /**
   * Plan § 4.1 round-2 BLOCKER 1 fix. Confirm Edit Verse must
   * clear `selectedVerse` + `includeVerse` before routing to Q1,
   * matching the Prev-clears-verse rule in § 3.2. A bare
   * goTo('bibleVerse') would route to Q1 with the stale verse
   * still committed.
   */
  const handleEditVerse = () => {
    setSelectedVerse(null);
    setIncludeVerse(false);
    goTo('bibleVerse');
  };

  /**
   * Confirm send. Known-sender (show-my-name) is the only mode.
   * Fires onContinue with the redacted payload and closes the
   * sheet. This is the preview-stub path: Phase 3 swaps it to a
   * real submitShowMyNameCheckout call wired to
   * /api/commerce/checkout.
   */
  const handleConfirmSend = () => {
    if (!authorizeOutbound()) return;
    onContinue({
      prayerId: prayer.id,
      includesCardLabel: includeCardLabel,
      verseCount: includeVerse && selectedVerse ? 1 : 0,
      recipientPresent: true,
      senderPresent: true,
    });
    onClose();
  };

  /**
   * Plan § 3.2 Prev navigation. The verse-path Prev clears
   * selectedVerse + includeVerse; the form-path Prev preserves
   * typed values (round-2 BLOCKER 2 mitigation — form-state
   * preservation across Edit navigation). Q1 (bibleVerse) is the
   * first step (Q0 mode screen removed), so it has no Prev.
   */
  const handlePrev = () => {
    if (surface === 'versePicker') {
      // One internal picker step first (found/browse -> fork); only at
      // the picker's root does Prev exit the verse step (the goBack
      // imperative handle owns the internal levels, plan decision 8).
      if (versePickerRef.current?.goBack()) return;
      setSelectedVerse(null);
      setIncludeVerse(false);
      setSurface('preview');
      setQuestion('bibleVerse');
      return;
    }
    if (question === 'cardLabel') {
      setQuestion('bibleVerse');
      return;
    }
    if (surface === 'recipientForm') {
      // Back to Q2 if the prayer has a cardLabel noun; back to Q1
      // otherwise.
      if (cardLabelNoun !== null) {
        setQuestion('cardLabel');
        setSurface('preview');
      } else {
        setQuestion('bibleVerse');
        setSurface('preview');
      }
      return;
    }
    if (surface === 'senderForm') {
      setQuestion('recipient');
      setSurface('recipientForm');
      return;
    }
    if (question === 'confirm') {
      setQuestion('sender');
      setSurface('senderForm');
      return;
    }
  };

  const showPrev =
    surface === 'versePicker' ||
    question === 'cardLabel' ||
    surface === 'recipientForm' ||
    surface === 'senderForm' ||
    question === 'confirm';

  const sheetTitle = t('mailCard.sheetTitle');

  // VersePicker needs top-aligned scroll for its sticky topic-row
  // headers. Form surfaces also use top-aligned scroll so long
  // forms don't center-collapse on tall viewports.
  const useScrollBodyClass =
    surface === 'versePicker' ||
    surface === 'recipientForm' ||
    surface === 'senderForm';
  const bodyClass = useScrollBodyClass
    ? `${styles.body} ${styles.bodyScroll}`
    : styles.body;

  // Preview-row props: thread the live recipient + sender names
  // through to MailCardPreview so it re-renders cumulatively as
  // the user walks through Q3 + Q4. Plan § 6.
  const previewRecipientName = recipientName.trim() || null;
  const previewSenderName = senderName.trim() || null;

  return (
    <div
      className={styles.root}
      style={{ zIndex }}
      role="dialog"
      aria-modal="true"
      aria-label={sheetTitle}
    >
      <header className={styles.header}>
        <button
          type="button"
          className={styles.headerBtn}
          onClick={onClose}
          aria-label={t('nav.cancel')}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
          <span className={styles.headerBtnLabel}>
            {t('nav.cancel')}
          </span>
        </button>
        <h1 className={styles.headerTitle}>{sheetTitle}</h1>
      </header>
      <div
        ref={noticeReserveRef}
        className={styles.noticeReserve}
        style={{ height: noticeReserve }}
        data-notice-reserve={noticeReserve}
        aria-hidden="true"
      />

      {authPhase === 'checking' ? (
        <div className={styles.body}>
          <div className={styles.loadingPad}>
            <div className={styles.loadingDot} aria-hidden="true" />
          </div>
        </div>
      ) : surface === 'authGate' ? (
        <div className={styles.body}>
          <MailCardAuthGate
            onSignInRequest={() => setAuthModalOpen(true)}
          />
        </div>
      ) : surface === 'versePicker' ? (
        <div className={bodyClass}>
          <ShareVersePicker
            ref={versePickerRef}
            onPick={handleVersePick}
            findPrayer={verseFindEligible ? prayer : undefined}
          />
        </div>
      ) : surface === 'recipientForm' ? (
        <div className={bodyClass}>
          <MailCardPreview
            prayer={prayer}
            includeVerse={includeVerse}
            selectedVerse={selectedVerse}
            includeCardLabel={includeCardLabel}
            cardLabelNoun={cardLabelNoun}
            recipientName={previewRecipientName}
            senderName={null}
          />
          <MailCardRecipientForm
            value={{ name: recipientName, address: recipientAddress }}
            onChange={handleRecipientChange}
            onSubmit={handleRecipientSubmit}
          />
        </div>
      ) : surface === 'senderForm' ? (
        <div className={bodyClass}>
          <MailCardPreview
            prayer={prayer}
            includeVerse={includeVerse}
            selectedVerse={selectedVerse}
            includeCardLabel={includeCardLabel}
            cardLabelNoun={cardLabelNoun}
            recipientName={previewRecipientName}
            senderName={previewSenderName}
          />
          <MailCardSenderForm
            value={{ name: senderName, address: senderAddress }}
            onChange={handleSenderChange}
            onSubmit={handleSenderSubmit}
          />
        </div>
      ) : (
        <div className={bodyClass}>
          <MailCardPreview
            prayer={prayer}
            includeVerse={includeVerse}
            selectedVerse={selectedVerse}
            includeCardLabel={includeCardLabel}
            cardLabelNoun={cardLabelNoun}
            recipientName={previewRecipientName}
            senderName={
              question === 'confirm' ? previewSenderName : null
            }
          />
          {question === 'bibleVerse' && (
            <MailCardVerseQuestion
              onInclude={handleVerseInclude}
              onSkip={handleVerseSkip}
            />
          )}
          {question === 'cardLabel' && cardLabelNoun !== null && (
            <MailCardCardLabelQuestion
              cardLabelNoun={cardLabelNoun}
              onInclude={handleCardLabelCommit(true)}
              onSkip={handleCardLabelCommit(false)}
            />
          )}
          {question === 'confirm' && (
            <MailCardConfirmReview
              selectedVerse={includeVerse ? selectedVerse : null}
              includeCardLabel={includeCardLabel}
              cardLabelNoun={cardLabelNoun}
              recipientName={recipientName}
              recipientAddress={recipientAddress}
              senderName={senderName}
              senderAddress={senderAddress}
              onEditVerse={handleEditVerse}
              onEditCardLabel={() => goTo('cardLabel')}
              onEditRecipient={() => goTo('recipient')}
              onEditSender={() => goTo('sender')}
              onSend={handleConfirmSend}
            />
          )}
        </div>
      )}

      {showPrev && (
        <div className={styles.prevRow}>
          <button
            type="button"
            className={styles.prevBtn}
            onClick={handlePrev}
            aria-label={t('nav.back')}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
        </div>
      )}

      {authModalOpen && (
        <AuthModal onClose={() => setAuthModalOpen(false)} zIndex={zMailcardAuth} />
      )}
    </div>
  );
}
